import React, { useLayoutEffect, useEffect, useState } from 'react';
import {
  Container, Grid, Card, CardContent, CardMedia, Chip,
  Typography, Button, Box, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Dialog, DialogContent, DialogActions, IconButton, Tooltip, useMediaQuery, useTheme, Fade, Zoom
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { isDiscountActive, calculateDiscountedPrice, getDiscountBadge } from '../utils/discountUtils';
import noPhoto from "../assets/no_photo.webp";
import ProductOptionsDialog from './ProductOptionsDialog';
import { Category as CategoryIcon,LocalFireDepartment as LocalFireDepartmentIcon } from '@mui/icons-material';
import SimpleARViewer from '../components/ARViewer';
import ProductImage from '../components/ProductImage';
import LimitInfoModal from './LimitInfoModal';
import InfoIcon from '@mui/icons-material/Info';

const ProductCard = ({ product, addToCart, keywordsData, language, deliverStatus, tableScan, setSelectedPro }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const isDiscounted = isDiscountActive(product);
  const discountedPrice = calculateDiscountedPrice(product);
  const discountBadge = getDiscountBadge(product);
  const [open, setOpen] = useState(false);
  const [arViewerOpen, setArViewerOpen] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);

  const handleOpenLimitModal = (e) => {
    e.stopPropagation();
    setLimitModalOpen(true);
  };
  
  const availbleTxt = language === 'ar' ? 'متوفر' : 'Available';
  const unAvailbleTxt = language === 'ar' ? 'غير متوفر' : 'Unavailable';
  const addToCartTxt = language === 'ar' ? 'أضف إلى السلة' : 'Add to basket';
  const addToCartithSaveTxt = language === 'ar' ? 'أضف إلى السلة - خصم!' : 'Save - Add to basket';
  const outOfStockTxt = language === 'ar' ? 'إنتهى من المخزن' : 'Out of stock';
  const reviewProductTxt = language === 'ar' ? 'يرجى مراجعة المنتج قبل الاستخدام' : 'Please review the product before use';
  const noAddDescribTxt = language === 'ar' ? 'لا توجد تفاصيل إضافية متاحة' : 'No additional description is available for this product';
  const noDescribTxt = language === 'ar' ? 'لا يوجد وصف متوفر لهذا المنتج' : 'No description is available for this product';
  
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const closeLimitModel = () => {
    setLimitModalOpen(false);
  };

  const handleAddToCart = () => {
    console.log('Product limit being passed:', product.product_limit);
    addToCart(product.id, 1, null, [], product.product_limit, product?.category?.card_limit);
    handleClose();
  };

  const handleCustomizeClick = () => {
    setSelectedProduct({
      ...product,
      final_price: discountedPrice || product.price
    });
    setSelectedPro({
      ...product,
      final_price: discountedPrice || product.price
    });
    setOptionsOpen(true);
  };

  const handleAddCustomizedToCart = (customizedItem) => {
    addToCart(customizedItem, 1, null, [], product?.product_limit, product?.category?.card_limit);
  };

  // Responsive font sizes and spacing
  const cardTitleFont = isMobile ? '0.9rem' : '1rem';
  const cardPriceFont = isMobile ? '1.2rem' : '1.6rem';
  const cardDescLines = isMobile ? 2 : 3;
  const cardHeight = isMobile ? 'auto' : '100%';

  return (
    <>
      <Grid 
        item 
        key={`${product.id}-${product.created_at}`} 
        xs={6} 
        sm={6} 
        md={4} 
        lg={3}
        sx={{ display: 'flex', justifyContent: 'center' }}
      >
        <Zoom in timeout={300}>
          <Card
            sx={{
              width: "100%",
              maxWidth: "100%",
              height: cardHeight,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
              borderRadius: isMobile ? "16px" : "22px",
              background: "linear-gradient(145deg, rgba(20,20,20,0.95), rgba(10,10,10,0.95))",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,215,0,0.15)",
              boxShadow: "0 10px 35px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)",
              transition: "all .35s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: 'pointer',
              '&:hover': {
                transform: isMobile ? "translateY(-4px) scale(1.01)" : "translateY(-10px) scale(1.02)",
                boxShadow: "0 16px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,215,0,0.3)",
              },
            }}
            onClick={() => {
              if (product.has_sizes || product.has_extras) {
                handleCustomizeClick();
              }
            }}
          >
            {/* Discount Badge */}
            {discountBadge && (
              <Box
                sx={{
                  position: 'absolute',
                  top: isMobile ? 8 : 12,
                  right: isMobile ? 8 : 12,
                  zIndex: 1,
                  backgroundColor: '#FFD700',
                  color: '#000000',
                  fontSize: isMobile ? 10 : 12,
                  fontWeight: 800,
                  padding: isMobile ? '4px 8px' : '6px 12px',
                  borderRadius: '20px',
                  border: '2px solid #000000',
                  boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
                  letterSpacing: '0.5px'
                }}
              >
                {discountBadge}
              </Box>
            )}

            <ProductImage
              product={product}
              language={language}
              onQuickView={handleClickOpen}
              onARView={() => setArViewerOpen(true)}
              noPhoto={noPhoto}
            />

            <CardContent sx={{ flexGrow: 1, p: isMobile ? 1.5 : 2, backgroundColor: 'transparent' }}>
              {/* Category */}
              {product.category && (
                <Chip
                  label={language === 'ar' ? product.category.name : product.category.name_en}
                  size="small"
                  sx={{
                    backgroundColor: '#1A1A1A',
                    color: '#B3B3B3',
                    border: '1px solid #2D2D2D',
                    borderRadius: '6px',
                    mb: 1,
                    height: isMobile ? 20 : 24,
                    fontSize: isMobile ? '0.65rem' : '0.75rem',
                    '&:hover': {
                      border: '1px solid #FFD700',
                      color: '#FFD700',
                    }
                  }}
                />
              )}
              
              {/* Product Name with Kcal Badge */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5, mb: 0.5 }}>
                <Typography 
                  variant="h6" 
                  component="div"
                  sx={{ 
                    fontWeight: 700,
                    lineHeight: 1.3,
                    height: isMobile ? 40 : 55,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: isMobile ? 2 : 2,
                    WebkitBoxOrient: 'vertical',
                    color: '#FFFFFF',
                    flex: 1,
                    fontSize: cardTitleFont,
                  }}
                >
                  {language === 'ar' ? product.name : product.name_en}
                </Typography>
                
                {/* Kcal Badge */}
                {product.kcal > 0 && (
                  <Chip 
                    label={`${product.kcal} ${language === 'ar' ? 'سعرة' : 'kcal'}`}
                    size="small"
                    sx={{
                      backgroundColor: '#cd3709',
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: isMobile ? '0.6rem' : '0.7rem',
                      height: isMobile ? 22 : 28,
                      borderRadius: '20px',
                      '& .MuiChip-label': {
                        px: isMobile ? 1 : 1.5,
                        py: 0.5,
                      },
                      boxShadow: '0 2px 8px rgba(255, 61, 0, 0.3)',
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>

              {/* Promotion Badge */}
              {product?.has_promotion && product?.promotion_is_active === true && (
                <Tooltip title={language === 'ar' ? 'هدية مجانية' : 'free gift'}>
                  <Chip
                    icon={<CardGiftcardIcon sx={{ fontSize: isMobile ? 14 : 16 }} />}
                    label={language === 'ar' ? 'هدية مجانية' : 'Free gift'}
                    size="small"
                    sx={{
                      mt: 0.5,
                      mb: 0.5,
                      backgroundColor: 'rgba(255, 215, 0, 0.15)',
                      color: '#FFD700',
                      border: '1px solid #FFD700',
                      fontWeight: 600,
                      height: isMobile ? 22 : 26,
                      fontSize: isMobile ? '0.65rem' : '0.7rem',
                      '& .MuiChip-icon': {
                        color: '#FFD700',
                        fontSize: isMobile ? 14 : 16
                      }
                    }}
                  />
                </Tooltip>
              )}

              {/* Description - Hide on mobile to save space */}
              {!isMobile && (
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{
                    mb: 1.5,
                    height: 60,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: cardDescLines,
                    WebkitBoxOrient: 'vertical',
                    color: '#B3B3B3',
                    fontSize: '0.75rem'
                  }}
                >
                  {language === 'ar' ? product.description : product.description_en}
                </Typography>
              )}
              
              {/* Price Display */}
              <Box sx={{ mt: 'auto' }}>
                {isDiscounted ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Typography
                        variant="h5"
                        fontWeight="900"
                        sx={{
                          color: "#FFD700",
                          fontSize: cardPriceFont,
                          letterSpacing: ".5px",
                        }}
                      >
                        {discountedPrice.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          textDecoration: 'line-through', 
                          color: '#6B6B6B',
                          fontSize: isMobile ? '0.7rem' : '0.8rem'
                        }}
                      >
                        {product.price.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                      </Typography>
                    </Box>
                    {!isMobile && (
                      <Chip
                        label={language === 'ar' ? `وفر ${product.discount_percent}%` : `Save ${product.discount_percent}%`} 
                        size="small"
                        sx={{
                          backgroundColor: '#FFD700',
                          color: '#000000',
                          fontWeight: 'bold',
                          height: 22,
                          fontSize: '0.7rem',
                          border: '1px solid #000000',
                          mt: 0.5
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  <Typography
                    variant="h5"
                    fontWeight="900"
                    sx={{
                      color: "#FFD700",
                      fontSize: cardPriceFont,
                      letterSpacing: ".5px",
                    }}
                  >
                    {product.price.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                )}
                
                {/* Limit Info */}
                {(product.product_limit > 0 || product?.category?.card_limit > 0) && tableScan ? (
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 0.5,
                      backgroundColor: 'rgba(255, 111, 0, 0.1)',
                      borderRadius: '20px',
                      px: 0.8,
                      py: 0.3,
                      mt: 0.5,
                      width: 'fit-content'
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#ff6f00",
                        fontSize: isMobile ? "0.6rem" : "0.7rem",
                        fontWeight: 600,
                      }}
                    > 
                      {language === 'ar' ? 'الحد الأقصى' : 'Max'} 
                      {' '}
                      {product?.category?.card_limit > 0 ? product?.category?.card_limit : product.product_limit} 
                      {' '}
                      {language === 'ar' ? 'قطعة' : 'pcs'}
                    </Typography>
                    
                    <IconButton
                      size="small"
                      onClick={handleOpenLimitModal}
                      sx={{
                        color: "#ff6f00",
                        padding: '2px',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 111, 0, 0.2)'
                        }
                      }}
                    >
                      <InfoIcon sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }} />
                    </IconButton>
                  </Box>
                ) : null}
              </Box>
            </CardContent>
            
            {/* Add to Cart Button */}
            <Box sx={{ p: isMobile ? 1.5 : 2, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                onClick={(e) => {
                  e.stopPropagation();
                  if (product.has_sizes || product.has_extras) {
                    handleCustomizeClick();
                  } else {
                    addToCart(product.id, 1, null, [], product?.product_limit, product?.category?.card_limit);
                  }
                }}
                disabled={product.quantity <= 0 || (!deliverStatus && !tableScan)}
                startIcon={product.quantity > 0 && <AddShoppingCartIcon sx={{ fontSize: isMobile ? 16 : 18 }} />}
                sx={{
                  borderRadius: "30px",
                  py: isMobile ? 1 : 1.4,
                  fontWeight: 700,
                  fontSize: isMobile ? '0.75rem' : '0.85rem',
                  letterSpacing: ".5px",
                  background: isDiscounted ? "linear-gradient(135deg, #4CAF50, #63b405)" : "linear-gradient(135deg,#FFD700,#FFC400)",
                  color: "#000",
                  boxShadow: "0 6px 16px rgba(255,215,0,0.35)",
                  transition: "all .25s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 25px rgba(255,215,0,0.55)",
                  },
                  "&:disabled": {
                    background: "#1a1a1a",
                    color: "#777",
                    boxShadow: "none",
                  },
                }}
              >
                {product.quantity <= 0 ? outOfStockTxt : (
                  (product.has_sizes || product.has_extras) 
                    ? (isMobile ? (language === 'ar' ? 'تخصيص' : 'Custom') : (language === 'ar' ? 'تخصيص' : 'Customize'))
                    : (isDiscounted ? (isMobile ? 'وفر - أضف' : addToCartithSaveTxt) : addToCartTxt)
                )}
              </Button>
            </Box>
          </Card>
        </Zoom>
      </Grid>

{/* Product Detail Dialog - Modern Responsive Design */}
<Dialog 
  open={open} 
  onClose={handleClose} 
  maxWidth="md" 
  fullWidth 
  fullScreen={isMobile}
  dir={language === 'ar' ? 'rtl' : 'ltr'}
  PaperProps={{
    sx: {
      backgroundColor: '#0D0D0D',
      background: 'linear-gradient(135deg, #0D0D0D 0%, #1A1A1A 100%)',
      border: '1px solid rgba(255, 215, 0, 0.2)',
      borderRadius: isMobile ? 0 : '24px',
      boxShadow: '0 20px 40px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,215,0,0.1)',
      overflow: 'hidden',
      position: 'relative',
    }
  }}
>
  {/* Conditional Layout: Mobile vs PC */}
  {isMobile ? (
    // ========== MOBILE LAYOUT ==========
    <>
      {/* Hero Image Section - Full width on mobile */}
      <Box sx={{ 
        position: 'relative',
        width: '100%',
        height: '35vh',
        backgroundColor: '#0A0A0A',
        overflow: 'hidden'
      }}>
        <CardMedia
          component="img"
          image={product.imageUrl || noPhoto}
          alt={language === 'ar' ? product.name : product.name_en}
          sx={{ 
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        
        {/* Gradient Overlay for better text readability */}
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%',
          background: 'linear-gradient(to top, rgba(13,13,13,1) 0%, rgba(13,13,13,0) 100%)',
        }} />
        
        {/* Close Button - Floating */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: '#FFFFFF',
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255,215,0,0.3)',
            zIndex: 2,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#FFD700',
              color: '#000000',
              border: '1px solid #FFD700',
              transform: 'rotate(90deg)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        
        {/* Discount Badge - Floating */}
        {isDiscounted && (
          <Box sx={{
            position: 'absolute',
            left: 16,
            top: 16,
            zIndex: 2,
          }}>
            <Chip 
              label={language === 'ar' ? `-${product.discount_percent}%` : `-${product.discount_percent}%`}
              sx={{
                backgroundColor: '#FFD700',
                color: '#000000',
                fontWeight: 'bold',
                fontSize: '1rem',
                height: 36,
                '& .MuiChip-label': { px: 2 },
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            />
          </Box>
        )}
      </Box>

      {/* Content Section - Mobile */}
      <DialogContent sx={{ 
        p: 3, 
        backgroundColor: '#0D0D0D',
        pt: 2,
      }}>
        {/* Title */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ 
            color: '#FFD700',
            fontWeight: 800,
            fontSize: '1.5rem',
            lineHeight: 1.2,
            letterSpacing: '-0.5px',
          }}>
            {language === 'ar' ? product.name : product.name_en}
          </Typography>
          
          {product.category && (
            <Chip 
              label={language === 'ar' ? product.category.name : product.category.name_en}
              icon={<CategoryIcon sx={{ fontSize: 16 }} />}
              sx={{
                backgroundColor: 'rgba(255,215,0,0.1)',
                color: '#FFD700',
                border: '1px solid rgba(255,215,0,0.3)',
                fontWeight: 600,
                borderRadius: '20px',
                height: 28,
                '& .MuiChip-label': { fontSize: '0.75rem', px: 1.5 }
              }}
            />
          )}
        </Box>

        {/* Description */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ 
            lineHeight: 1.6, 
            textAlign: 'justify',
            color: '#E0E0E0',
            fontSize: '0.9rem',
          }}>
            {language === 'ar' ? product.description : product.description_en || noDescribTxt}
          </Typography>
        </Box>
        
        {/* Price Card */}
        <Box sx={{ 
          mb: 3, 
          p: 2.5, 
          background: 'linear-gradient(135deg, #1A1A1A 0%, #141414 100%)',
          border: '1px solid rgba(255,215,0,0.2)',
          borderRadius: '20px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,215,0,0.1) 0%, rgba(255,215,0,0) 70%)',
          }} />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            {isDiscounted ? (
              <Box>
                <Typography 
                  variant="h3" 
                  fontWeight="900"
                  sx={{ 
                    color: '#FFD700',
                    fontSize: '2rem',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    flexWrap: 'wrap'
                  }}
                >
                  {discountedPrice}
                  <Typography component="span" sx={{ 
                    fontSize: '0.9rem', 
                    fontWeight: 500,
                    color: '#B3B3B3'
                  }}>
                    {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      textDecoration: 'line-through', 
                      color: '#888888',
                      fontSize: '0.9rem'
                    }}
                  >
                    {product.price.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                  <Chip 
                    label={language === 'ar' ? `وفر ${product.discount_percent}%` : `Save ${product.discount_percent}%`}
                    size="small"
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      fontWeight: 'bold',
                      height: 24,
                      fontSize: '0.7rem'
                    }}
                  />
                </Box>
              </Box>
            ) : (
              <Typography variant="h3" fontWeight="900" sx={{ 
                color: '#FFD700', 
                fontSize: '2rem',
                display: 'flex',
                alignItems: 'baseline',
                gap: 1
              }}>
                {product.price.toFixed(2)}
                <Typography component="span" sx={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 500,
                  color: '#B3B3B3'
                }}>
                  {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                </Typography>
              </Typography>
            )}
          </Box>
        </Box>

        {/* Additional Info */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap'
        }}>
          {product.kcal > 0 && (
            <Chip 
              icon={<LocalFireDepartmentIcon sx={{ fontSize: 16 }} />}
              label={`${product.kcal} ${language === 'ar' ? 'سعرة' : 'kcal'}`}
              sx={{
                backgroundColor: 'rgba(255,215,0,0.1)',
                color: '#FFD700',
                borderRadius: '12px',
                '& .MuiChip-icon': { color: '#FFD700' }
              }}
            />
          )}
          {product.quantity <= 10 && product.quantity > 0 && (
            <Chip 
              label={language === 'ar' ? `متبقي ${product.quantity} فقط` : `Only ${product.quantity} left`}
              sx={{
                backgroundColor: 'rgba(255,61,0,0.15)',
                color: '#FF3D00',
                borderRadius: '12px',
              }}
            />
          )}
        </Box>
      </DialogContent>
    </>
  ) : (
    // ========== PC LAYOUT ==========
    <>
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 16,
          color: '#B3B3B3',
          backgroundColor: '#1A1A1A',
          border: '1px solid #2D2D2D',
          zIndex: 1,
          '&:hover': {
            backgroundColor: '#FFD700',
            color: '#000000',
            border: '1px solid #FFD700',
          }
        }}
      >
        <CloseIcon />
      </IconButton>
      
      <DialogContent sx={{ p: 4, backgroundColor: '#0D0D0D' }}>
        <Grid container spacing={4}>
          {/* Image Column - PC */}
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative' }}>
              <Box sx={{
                backgroundColor: '#1A1A1A',
                borderRadius: '16px',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 400,
                border: '1px solid #2D2D2D',
              }}>
                <CardMedia
                  component="img"
                  image={product.imageUrl || noPhoto}
                  alt={language === 'ar' ? product.name : product.name_en}
                  sx={{ 
                    objectFit: 'contain', 
                    borderRadius: '12px',
                    maxHeight: 380,
                    width: 'auto',
                    maxWidth: '100%'
                  }}
                />
              </Box>
              
              {/* Discount Badge - PC */}
              {isDiscounted && (
                <Chip 
                  label={language === 'ar' ? `-${product.discount_percent}%` : `-${product.discount_percent}%`}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: -8,
                    backgroundColor: '#FFD700',
                    color: '#000000',
                    fontWeight: 'bold',
                    fontSize: '0.8rem',
                    height: 32,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    zIndex: 2
                  }}
                />
              )}
            </Box>
          </Grid>
          
          {/* Content Column - PC */}
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ 
              color: '#FFD700',
              fontWeight: 900,
              fontSize: '2.2rem',
              mb: 2
            }}>
              {language === 'ar' ? product.name : product.name_en}
            </Typography>
            
            {product.category && (
              <Chip 
                label={language === 'ar' ? product.category.name : product.category.name_en}
                sx={{
                  backgroundColor: '#1A1A1A',
                  color: '#FFD700',
                  border: '1px solid #FFD700',
                  fontWeight: 600,
                  mb: 2,
                  borderRadius: '8px'
                }}
              />
            )}
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" sx={{ 
                lineHeight: 1.6, 
                textAlign: 'justify',
                color: '#FFFFFF',
                fontSize: '1rem',
                mb: 2
              }}>
                {language === 'ar' ? product.description : product.description_en || noDescribTxt}
              </Typography>
            </Box>
            
            {/* Price Card - PC */}
            <Box sx={{ 
              mb: 2, 
              p: 3, 
              backgroundColor: '#1A1A1A',
              border: '1px solid #2D2D2D',
              borderRadius: '16px'
            }}>
              {isDiscounted ? (
                <Box>
                  <Typography 
                    variant="h2" 
                    fontWeight="900"
                    sx={{ 
                      color: '#FFD700',
                      fontSize: '2.5rem',
                      lineHeight: 1.2
                    }}
                  >
                    {discountedPrice} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      textDecoration: 'line-through', 
                      color: '#6B6B6B',
                      mt: 1,
                      fontSize: '1.25rem'
                    }}
                  >
                    {product.price.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                  </Typography>
                  <Chip 
                    label={language === 'ar' ? `وفر ${product.discount_percent}%` : `Save ${product.discount_percent}%`}
                    sx={{
                      backgroundColor: '#FFD700',
                      color: '#000000',
                      fontWeight: 'bold',
                      mt: 2,
                      px: 2,
                      py: 1,
                      height: 'auto',
                      fontSize: '1rem'
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="h2" fontWeight="900" sx={{ color: '#FFD700', fontSize: '2.5rem' }}>
                  {product.price.toFixed(2)} {language === 'ar' ? keywordsData.carance_txt_ar : keywordsData.carance_txt_en}
                </Typography>
              )}
            </Box>

            {/* Additional Info - PC */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2,
              flexWrap: 'wrap'
            }}>
              {product.kcal > 0 && (
                <Chip 
                  icon={<LocalFireDepartmentIcon sx={{ fontSize: 16 }} />}
                  label={`${product.kcal} ${language === 'ar' ? 'سعرة' : 'kcal'}`}
                  sx={{
                    backgroundColor: 'rgba(255,215,0,0.1)',
                    color: '#FFD700',
                    borderRadius: '12px',
                  }}
                />
              )}
              {product.quantity <= 10 && product.quantity > 0 && (
                <Chip 
                  label={language === 'ar' ? `متبقي ${product.quantity} فقط` : `Only ${product.quantity} left`}
                  sx={{
                    backgroundColor: 'rgba(255,61,0,0.15)',
                    color: '#FF3D00',
                    borderRadius: '12px',
                  }}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </>
  )}
  
  {/* Actions Section - Works for both mobile and PC */}
  <DialogActions sx={{ 
    p: isMobile ? 3 : 3, 
    backgroundColor: '#0D0D0D',
    borderTop: '1px solid rgba(255,215,0,0.1)',
    flexDirection: isMobile ? 'column' : 'row',
    gap: 1.5,
  }}>
    <Button 
      onClick={handleClose} 
      fullWidth={isMobile}
      sx={{
        color: '#B3B3B3',
        fontWeight: 600,
        py: isMobile ? 1 : 1,
        px: 3,
        borderRadius: '40px',
        textTransform: 'none',
        fontSize: isMobile ? '0.9rem' : '1rem',
        '&:hover': {
          color: '#FFD700',
          backgroundColor: 'rgba(255, 215, 0, 0.08)',
        }
      }}
    >
      {language === 'ar' ? 'إغلاق' : 'Close'}
    </Button>
    
    <Button 
      onClick={() => {
        if (product.has_sizes || product.has_extras) {
          handleCustomizeClick();
          handleClose();
        } else {
          handleAddToCart();
        }
      }}
      variant="contained" 
      disabled={product.quantity <= 0 || (!deliverStatus && !tableScan)}
      startIcon={<AddShoppingCartIcon />}
      fullWidth={isMobile}
      sx={{
        background: product.quantity > 0 ? 'linear-gradient(135deg, #FFD700 0%, #F5C800 100%)' : '#1A1A1A',
        color: '#000000',
        fontWeight: 800,
        py: isMobile ? 1.2 : 1.2,
        px: isMobile ? 2 : 4,
        borderRadius: '40px',
        fontSize: isMobile ? '0.95rem' : '1rem',
        textTransform: 'none',
        transition: 'all 0.2s ease',
        boxShadow: product.quantity > 0 ? '0 4px 12px rgba(255,215,0,0.3)' : 'none',
        '&:hover': {
          background: product.quantity > 0 ? 'linear-gradient(135deg, #FFE55C 0%, #FFD700 100%)' : '#1A1A1A',
          transform: product.quantity > 0 ? 'translateY(-2px)' : 'none',
          boxShadow: product.quantity > 0 ? '0 6px 16px rgba(255,215,0,0.4)' : 'none',
        },
        '&:disabled': {
          backgroundColor: '#1A1A1A',
          color: '#6B6B6B',
          boxShadow: 'none',
        }
      }}
    >
      {product.quantity <= 0 ? outOfStockTxt : (
        (product.has_sizes || product.has_extras) 
          ? (language === 'ar' ? 'تخصيص' : 'Customize')
          : (isDiscounted ? addToCartithSaveTxt : addToCartTxt)
      )}
    </Button>
  </DialogActions>
</Dialog>
      {/* Product Options Dialog */}
      <ProductOptionsDialog
        open={optionsOpen}
        onClose={() => setOptionsOpen(false)}
        product={selectedProduct}
        onAddToCart={handleAddCustomizedToCart}
        language={language}
        keywordsData={keywordsData}
      />
      
      {/* AR Viewer Dialog */}
      <SimpleARViewer
        open={arViewerOpen}
        onClose={() => setArViewerOpen(false)}
        product={product}
        language={language}
      />
      
      {/* Limit Info Modal */}
      <LimitInfoModal
        open={limitModalOpen}
        onClose={closeLimitModel}
        language={language}
        product={product}
      />
    </>
  );
};

export default ProductCard;